import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import Button from "@/components/wpl/button/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
export interface ResourceCardContentProps {
    header: string;
    description: string;
    buttonText: string;
    url: string;
    type?: string;
    color?: string;
}
interface ResourceCardStyle {
    padding?: string;
    borderStyle?: string;
    borderWidth?: string;
    borderRadius?: string;
    borderColor?: string;
    width?: string;
    height?: string;
    backgroundColor?: string;
}

export type ResourceCardType = "primary" | "secondary";

export type ResourceCardColor = "white" | "grey";

const resourceCardStyles: Record<
    ResourceCardType,
    Partial<Record<ResourceCardColor, ResourceCardStyle>>
> = {
    primary: {
        white: {
            padding: "24px",
            borderStyle: "solid",
            borderWidth: "1px",
            borderRadius: "10px",
            borderColor: "#c1c7d0",
            width: "310px"
        },
        grey: {
            height: "100%",
            padding: "40px",
            backgroundColor: "#fafbfc",
            borderRadius: "3px"
        }
    },
    secondary: {}
};

const wrapperStyle = css({
    position: "relative",
    boxSizing: "border-box",
    display: "block",
    float: "left",
    padding: "16px",
    minHeight: "1px",
    fontFamily: `"Charlie Text", -apple - system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans - serif`,
    fontSize: "16px",
    lineHeight: "16px",
    verticalAlign: "baseline",
    [media.above.sm]: {
        width: "33.33333%"
    }
});

const headerStyle = css({
    boxSizing: "border-box",
    display: "block",
    marginBlockEnd: "24px",
    fontFamily: `"Charlie Display", Arial, sans-serif`,
    fontSize: "1.25rem",
    fontWeight: "500",
    letterSpacing: ".02rem",
    lineHeight: "1.2",
    verticalAlign: "baseline",
    color: "#253858",
    [media.only.sm]: {
        marginBlockEnd: "14px"
    },
    [media.below.sm]: {
        fontSize: "1rem",
        marginBlockEnd: "14px",
        lineHeight: "1.25"
    }
});

const descriptionStyle = css({
    boxSizing: "border-box",
    display: "block",
    marginBlockEnd: "24px",
    fontFamily: `"Charlie Text", -apple - system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans - serif`,
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "1.5",
    color: "#42526e",
    verticalAlign: "baseline"
});

const buttonStyle = css({
    background: "#ffffff",
    color: "#0052cc",
    boxSizing: "border-box",
    display: "inline-block",
    height: "unset",
    minHeight: "1.875rem",
    paddingBlockEnd: "0.25rem",
    paddingInlineStart: "10px",
    paddingInlineEnd: "10px",
    paddingBlockStart: "0.25rem",
    fontFamily: `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
    fontSize: ".875rem",
    fontWeight: "500",
    lineHeight: "1.3",
    textAlign: "center",
    textDecorationLine: "none",
    textDecorationStyle: "solid",
    textDecorationThickness: "auto",
    textWrap: "wrap",
    verticalAlign: "baseline",
    whiteSpaceCollapse: "collapse",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    borderRadius: "3px",
    backgroundAttachment: "scroll",
    backgroundClip: "border-box",
    backgroundOrigin: "padding-box",
    backgroundSize: "auto"
});

const ResourceCard = ({
    header,
    description,
    buttonText,
    url,
    type,
    color
}: ResourceCardContentProps) => {
    const formattedType = type?.toLowerCase();
    const resourceCardType =
        formattedType === "secondary" ? "secondary" : "primary";

    const formattedColor = color?.toLowerCase();
    const resourceCardColor = formattedColor === "grey" ? "grey" : "white";

    const styles = resourceCardStyles[resourceCardType][resourceCardColor];
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const {
        padding,
        width,
        height,
        borderStyle,
        borderRadius,
        borderWidth,
        borderColor,
        backgroundColor
    } = styles ? styles : resourceCardStyles.primary.white!;
    /* eslint-enable @typescript-eslint/no-non-null-assertion */

    const backgroundStyle = css({
        boxSizing: "border-box",
        display: "block",
        flexDirection: "column",
        padding: padding,
        width: width,
        height: height,
        borderStyle: borderStyle,
        borderRadius: borderRadius,
        borderWidth: borderWidth,
        backgroundColor: backgroundColor,
        borderColor: borderColor
    });

    const templatesTabCardDataEventProps: DataEventProps = {
        "data-product-key": "enterpriseContactSales",
        "data-event-component": "linkButton",
        "data-event-container": "cardSection",
        "data-event": "clicked"
    };

    return (
        <div css={wrapperStyle}>
            <div css={backgroundStyle}>
                <div css={headerStyle}>{header}</div>
                <div css={descriptionStyle}>{description}</div>
                <div>
                    <Button
                        text={buttonText}
                        aria-label={buttonText}
                        css={buttonStyle}
                        url={url}
                        type="secondary"
                        color="whiteblue"
                        dataEventProps={templatesTabCardDataEventProps}
                    />
                </div>
            </div>
        </div>
    );
};

export default ResourceCard;
